import React, { useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom'; // add at top

import tpo from '../../../assets/PowellPage/Commercial Roofing/Commercial Roofing TPO Roofing.jpg';
import pvc from '../../../assets/PowellPage/Commercial Roofing/PVC_Roofing_powell_ohio.jpg';
import epdm from '../../../assets/PowellPage/Commercial Roofing/EPDM_Roofing_powell_ohio.jpg';
import coatings from '../../../assets/PowellPage/Commercial Roofing/Roof_Coatings_&_Maintenance.jpg';
import repairs from '../../../assets/PowellPage/Commercial Roofing/Commercial Roof Repairs.jpg';
import metal from '../../../assets/PowellPage/Commercial Roofing/Commercial_Metal_Roofing_Powell_Ohio.jpg';

import '../all/Main.css';
import './RoofingSection.css';

import View from './View';
import Badge from '../ServiceSection/Badge';

const SERVICES = [
  {
    title: "TPO Roofing",
    description: "TPO roofing solutions offer energy efficiency, durability, and weather resistance.",
    image: tpo,
    tag: "Commercial"
  },
  {
    title: "EPDM Roofing",
    description: "EPDM roofing systems provide superior waterproofing and long-term membrane protection.",
    image: epdm,
    tag: "Commercial"
  },
  {
    title: "PVC Roofing",
    description: "PVC roofing delivers chemical resistance, durability, and energy-efficient performance.",
    image: pvc,
    tag: "Commercial"
  },
  {
    title: "Roof Coatings & Maintenance",
    description: "Roof coatings and maintenance extend roof life, prevent leaks, and reduce repair costs.",
    image: coatings,
    tag: "Commercial"
  },
  {
    title: "Commercial Metal",
    description: "Commercial metal roofing offers strength, longevity, and superior weather resistance.",
    image: metal,
    tag: "Commercial"
  },
  {
    title: "Commercial Roof Repair",
    description: "Commercial roof repair services fix leaks, damage, and restore roof performance fast.",
    image: repairs,
    tag: "Commercial"
  }
];

const COLORS = {
  primary: '#3D3B6D',
  accent: '#b22335',
  border: '#e8e8ef',
  cardBg: '#f8f9fa',
  overlay: 'rgba(61, 59, 109, 0.65)'
};

const ScrollReveal = ({ children, delay = 0, direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const directionMap = {
    up: { y: 60 },
    left: { x: 60 },
    right: { x: -60 }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...directionMap[direction] }}
      animate={isInView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{ duration: 0.7, delay }}
    >
      {children}
    </motion.div>
  );
};

const ParallaxImage = ({ src, alt }) => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);

  return (
    <div ref={ref} className="position-relative overflow-hidden flex-shrink-0" style={{ height: '240px' }}>
      <motion.img
        src={src}
        alt={alt}
        className="w-100 h-100"
        style={{ objectFit: 'cover', y }}
      />
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          background: `linear-gradient(180deg, ${COLORS.overlay} 0%, rgba(26,26,46,.45) 100%)`
        }}
      />
    </div>
  );
};

const ServiceCard = ({ service, index }) => {
  const isEven = index % 2 === 0;

  return (
    <motion.div whileHover={{ y: -10, scale: 1.02 }} className="h-100 d-flex flex-column w-100">
      <div
        className="h-100 position-relative overflow-hidden d-flex flex-column"
        style={{
          borderRadius: '16px',
          backgroundColor: COLORS.cardBg,
          border: `1px solid ${COLORS.border}`
        }}
      >
        <ParallaxImage src={service.image} alt={service.title} />

        <div className="position-absolute" style={{ top: '14px', left: '14px', zIndex: 2 }}>
          <span
            className="comm-badge"
            style={{
              background: isEven ? COLORS.accent : COLORS.primary
            }}
          >
            {service.tag}
          </span>
        </div>

        <div className="p-4 d-flex flex-column flex-grow-1">
          <h3 className=" m-0 mb-2 comm-card-title">
            {service.title}
          </h3>

          <p className="comm-card-desc flex-grow-1">
            {service.description}
          </p>

          <Link to="/commercial-roofing" className="comm-card-link mt-auto">
            Learn More
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="ms-2"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Link>

        </div>

        <div
          className="position-absolute bottom-0 start-0"
          style={{
            height: '3px',
            width: '100%',
            background: `linear-gradient(90deg, ${isEven ? COLORS.accent : COLORS.primary}, transparent)`
          }}
        />
      </div>
    </motion.div>
  );
};

const RoofingSection = () => {
  return (
    <section className="cs-section roofing-commercial-section">
      <Container>
        <div className="">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <ScrollReveal direction="left">
                <div className="cs-badge">
                  <Badge text={"Commercial Roofing"} />
                </div>
              </ScrollReveal>

              <ScrollReveal direction="left" delay={0.1}>
                <h2 className="cs-title comm-title">
                  Powell, Ohio Commercial <span>Roofing Solutions</span>
                </h2>
              </ScrollReveal>

              <ScrollReveal direction="left" delay={0.2}>
                <p className="cs-desc comm-desc">
                  We provide advanced commercial roofing systems designed for durability, performance, and long-term business protection in Powell, Ohio.
                </p>
              </ScrollReveal>
            </div>

            <View />
          </div>
        </div>

        <Row className="g-4 cs-content">
          {SERVICES.map((service, index) => (
            <Col lg={4} md={6} key={index} className="d-flex flex-column">
              <ServiceCard service={service} index={index} />
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default RoofingSection;
